import {
  SearchFilterSelectorContext,
  SearchFilterSelectorContextType,
} from "@/features/search-filter/context/searchFilterSelectorContext";
import { gql, useQuery } from "@apollo/client";
import { Global, css } from "@emotion/react";
import { ErrorScreen, LoadingScreen } from "@stenajs-webui/panels";
import * as React from "react";
import { useContext, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { PageLayout } from "@/components/page-layout/PageLayout";
import { useCustomer } from "../../../../../hooks/UseCustomer";
import { useNegotiationYears } from "../../../../../contexts/NegotiationYearGate";
import { CustomerViewInfoPairBoxes } from "../../../info-boxes/CustomerViewInfoPairBoxes";
import { useRouteAgreementsRatesByFilterQuery } from "../hooks/UseRouteAgreementsRatesByFilter";
import { BulkEditRatesHeader } from "./BulkEditRatesHeader";
import { BulkEditRatesTableWrapper } from "./BulkEditRatesTableWrapper";
import { transformFilterModelToFilterInput } from "./filter/transformFilterModelToFilterInput";
import {
  CustomerHeaderCustomersByIdFragment,
  CustomerIndexQuery,
  CustomerIndexQueryVariables,
} from "@/gql/graphql";
import { useFetchAllRoutePairs } from "@/features/search-filter/hooks/FilterFetchers/UseFetchAllRoutePairs";
import {
  bulkEditRatesStandardTableActions,
  bulkEditRatesActions,
} from "../redux/reducer";
import { bulkEditRatesStatBoxesActions } from "./stat-boxes/redux/reducer";
import { mergeBulkEditRatesStatBoxQueryData } from "./stat-boxes/transformers/BulkEditRatesStatBoxQueryDataMerger";
import { calculateCustomerStatisticsBulkEditRatesTable } from "./stat-boxes/transformers/BulkEditRatesStatBoxDataTransformer";
import { transformRatesByFilterQueryToBulkEditRatesPriceRow } from "../transformers/BulkEditRatesTransformers";
import { groupBy } from "lodash";
import { RoutePair } from "@/features/search-filter/types/FilterEntitys";

const query = gql`
  query CustomerIndex($customerId: ID!, $headAgreementId: ID!) {
    productPrice {
      customers {
        byId(id: $customerId) {
          id
          custIndex
          name
          custNo
          invoiceCurrency {
            id
            code
          }
          hasRebateAgreements
          salesRegion {
            id
            name
          }
          statisticalGrouping {
            id
          }
          seller {
            id
            name
          }
          custStatus {
            id
            name
          }
          isParent
          parent {
            id
          }
          childCustomers {
            id
            name
          }
        }
      }

      headAgreement {
        byId(headAgreementId: $headAgreementId) {
          id
          statusCode
          name
          headAgreementNumber
          breadCrumbName
          hasSomeRouteAgreementsWithMultiLeg
          statusCode
          rowVersion
          valid {
            start {
              isoString
            }
            end {
              isoString
            }
          }
          writeHistory {
            createdAt {
              isoString
            }
            createdBy {
              id
              name
            }
            modifiedAt {
              isoString
            }
            modifiedBy {
              id
              name
            }
          }
        }
      }
    }
  }
`;

interface FilterLoaderProps {
  negotiationYear: number;
  customer: CustomerHeaderCustomersByIdFragment | null | undefined;
  headAgreement:
    | CustomerIndexQuery["productPrice"]["headAgreement"]["byId"]
    | null
    | undefined;
  allRoutePairs: RoutePair[];
}

const BulkEditRatesFilterLoader = ({
  negotiationYear,
  customer,
  headAgreement,
  allRoutePairs,
}: FilterLoaderProps) => {
  const { customerId } = useParams() as { customerId: string };
  const dispatch = useDispatch();

  const { workspaceState, committedState } = useContext(
    SearchFilterSelectorContext
  ) as SearchFilterSelectorContextType;

  const queryRoutePairCodes =
    committedState.routePairs.length === 0
      ? allRoutePairs.map((routePair) => routePair.id)
      : committedState.routePairs.map((routePair) => routePair.id);

  const {
    fetchRouteAgreementsByFilter,
    routeAgreementRates,
    loading,
    error,
    refetch,
  } = useRouteAgreementsRatesByFilterQuery();

  const onSearch = () => {
    if (queryRoutePairCodes.length !== 0) {
      fetchRouteAgreementsByFilter({
        variables: {
          byFilterInput: transformFilterModelToFilterInput(
            headAgreement?.id ?? "",
            workspaceState.formModel
          ),
          year: negotiationYear,
          customerId,
          routePairCodes: queryRoutePairCodes,
        },
      });
    }
  };

  useEffect(() => {
    dispatch(bulkEditRatesStandardTableActions.clearSelection());
    dispatch(bulkEditRatesActions.revertAll());
    dispatch(bulkEditRatesStatBoxesActions.clearAll());
    onSearch();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (headAgreement && routeAgreementRates) {
      const bulkEditRatesPrices =
        routeAgreementRates?.productPrice?.routeAgreementPrice?.byFilter
          ?.agrRoutePrices ?? [];

      const customerStatItemsByRoutePairCodes =
        routeAgreementRates?.productPrice.customers.byId
          ?.statisticsByRoutePairCodes.itemsByRoutePairCodes;

      if (bulkEditRatesPrices) {
        const bulkEditRatesPricesTableRow = bulkEditRatesPrices.map(
          transformRatesByFilterQueryToBulkEditRatesPriceRow
        );

        dispatch(bulkEditRatesActions.clear());
        dispatch(bulkEditRatesActions.setState(bulkEditRatesPricesTableRow));
        dispatch(bulkEditRatesActions.setSaveChangesLoading(false));
      }

      const headAgreementId = headAgreement.id;
      if (customerStatItemsByRoutePairCodes && headAgreementId) {
        const groupByRoutePairCodes = groupBy(
          customerStatItemsByRoutePairCodes,
          (i) => i.routePairCode
        );

        const groupedList = Object.keys(groupByRoutePairCodes).map(
          (routePairCode) =>
            calculateCustomerStatisticsBulkEditRatesTable(
              groupByRoutePairCodes[routePairCode],
              customerId,
              routePairCode
            )
        );

        dispatch(
          bulkEditRatesStatBoxesActions.setQueryParams({
            customerId,
            routePairCodes: queryRoutePairCodes,
            year: negotiationYear,
          })
        );

        const queryData = mergeBulkEditRatesStatBoxQueryData(
          {},
          customerStatItemsByRoutePairCodes
        );
        dispatch(bulkEditRatesStatBoxesActions.setQueryData(queryData));

        dispatch(
          bulkEditRatesStatBoxesActions.setHeadAgreementId(headAgreementId)
        );
        dispatch(bulkEditRatesStatBoxesActions.setStatBoxesData(groupedList));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeAgreementRates]);

  return (
    <>
      <Global
        styles={css`
          body {
            display: table;
          }
        `}
      />
      <Helmet title="Edit rates - Customer - FPP" />
      <BulkEditRatesHeader
        custIndex={customer?.custIndex ?? " "}
        routeAgreementRates={routeAgreementRates}
        refetch={refetch}
        headAgreementNumber={String(headAgreement?.headAgreementNumber ?? "")}
      />
      <PageLayout>
        {customer && headAgreement && (
          <CustomerViewInfoPairBoxes
            customer={customer}
            headAgreement={headAgreement}
            headAgreementId={headAgreement.id}
            switchButtonDisabled={true}
            justifyContentRow={"flex-start"}
            customerPopoverEnabled
          />
        )}

        <BulkEditRatesTableWrapper
          loading={loading}
          error={error}
          onSearch={onSearch}
          routeAgreementRates={routeAgreementRates}
        />
      </PageLayout>
    </>
  );
};

export const BulkEditRatesFetcher: React.FC = () => {
  const { headAgreementId, customerId } = useParams() as {
    headAgreementId: string;
    customerId: string;
  };
  const { allRoutePairs, loading: loadingAllRoutes } = useFetchAllRoutePairs();

  // TODO: Create hook together with query
  const { data, loading: loadingCustomer } = useQuery<
    CustomerIndexQuery,
    CustomerIndexQueryVariables
  >(query, {
    variables: {
      customerId,
      headAgreementId,
    },
  });

  const customerIndex = data?.productPrice.customers.byId?.custIndex;
  const headAgreement = data?.productPrice.headAgreement.byId;

  const { defaultYear } = useNegotiationYears();
  const { customer } = useCustomer(customerId, defaultYear);

  const validFrom = headAgreement?.valid.start?.isoString;

  const negotiationYear = validFrom
    ? new Date(validFrom).getUTCFullYear()
    : new Date().getUTCFullYear();

  if (loadingCustomer || !customerIndex || loadingAllRoutes) {
    return <LoadingScreen />;
  } else if (
    !headAgreement ||
    !headAgreementId ||
    !customerId ||
    !allRoutePairs
  ) {
    const errorText = !headAgreement
      ? "No head agreement found for that id"
      : !headAgreementId
      ? "Missing headAgreementId."
      : !customerId
      ? "Missing customerId."
      : "";
    return <ErrorScreen text={errorText} />;
  } else {
    return (
      <BulkEditRatesFilterLoader
        negotiationYear={negotiationYear}
        customer={customer}
        headAgreement={headAgreement}
        allRoutePairs={allRoutePairs}
      />
    );
  }
};
